<template>
  <PopoverOptions :hide-delete="hideDelete" @add="handleAdd" @delete="handleDelete">
    <div class="card">
      <a-form ref="formRef" layout="vertical" :model="formState">
        <a-form-item class="input" label="Hacer" name="action_type" required>
          <a-select
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model:value="formState.action_type"
            placeholder="Seleccionar acción de la condición"
            :options="ActionOptions"
          />
        </a-form-item>

        <a-form-item label="Campo" name="input_name" required>
          <a-select
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model:value="formState.input_name"
            placeholder="Seleccionar un campo"
            :disabled="fieldOptions.length === 0"
          >
            <a-select-option
              v-for="field in fieldOptions"
              :key="field.name"
              :disabled="isOptionDisabled(field.type)"
            >
              {{ field.label }} <span class="type-name"> ({{ getNameByType(field.type) }}) </span>
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </div>
  </PopoverOptions>
</template>

<script setup lang="ts">
import { reactive, watch, computed, ref, inject } from "vue";
import type { Ref } from "vue";
import { FormInstance } from "ant-design-vue";

import { ActionType, Condition, ElementType, FormElement, FormElse } from "@/@types/entities/forms";
import { formConditionsManager } from "@/app/entities/utils/forms/FormConditionsManager";
import { getNameByType } from "@/app/entities/utils/forms";
import { ActionOptions } from "@/app/entities/utils/forms/conditionsSelectOptions";

import PopoverOptions from "@/app/entities/components/card/conditions/PopoverOptions.vue";

const props = defineProps<{
  formElse: FormElse;
  hideDelete: boolean;
  inputsList?: FormElement[];
}>();

const emit = defineEmits<{
  (e: "update:formElse", formElse: FormElse): void;
  (e: "add", id: string): void;
  (e: "delete", id: string): void;
}>();

const formState: FormElse = reactive({
  id: props.formElse.id,
  action_type: props.formElse.action_type,
  input_name: props.formElse.input_name,
  input_label: props.formElse.input_label,
});

const conditionBody = inject("conditionBody") as Ref<Condition | undefined>;

watch(formState, () => emit("update:formElse", formState));
watch(
  () => formState.input_name,
  () => {
    formState.input_label = selectedInputElement.value?.label;
  }
);

const handleAdd = () => emit("add", props.formElse.id);
const handleDelete = () => emit("delete", props.formElse.id);

const selectedInputElement = computed(() =>
  props.inputsList?.find((input) => input.name === formState.input_name)
);

const fieldOptions = computed(() =>
  formConditionsManager.getInputOptionsByElement("else", props.inputsList, conditionBody.value)
);

const isOptionDisabled = (type: ElementType) => {
  const inputsCanBeDisabled = [ElementType.Reminder, ElementType.Pdp];
  const actionsCanDisable = [
    ActionType.Disable,
    ActionType.Enable,
    ActionType.NoRequire,
    ActionType.Require,
  ];

  return formState.action_type
    ? inputsCanBeDisabled.includes(type) && actionsCanDisable.includes(formState.action_type)
    : false;
};

// Form validation
const formRef = ref<FormInstance | undefined>(undefined);

const validate = async () => {
  await formRef.value?.validateFields();
};

defineExpose({ validate });
</script>

<style scoped lang="sass">
.card
  width: 100%
  background-color: white
  border-radius: 4px
  padding: 20px

.ant-form-item
  margin: 0

.input
  margin-bottom: 20px

.type-name
  color: $gray-7
</style>
